import React, { useCallback, useMemo, useState } from 'react'
import { Link, useParams, Redirect, generatePath, RouteChildrenProps } from 'react-router-dom'
import Button from '../../../atoms/button/Button'
import Input from '../../../atoms/form/input/Input'
import Dropdown from '../../../atoms/form/input/Dropdown'
import useForm from '../../../../hooks/useForm'
import API from '../../../../services/api'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import DisplayField from '../../../atoms/form/display-field/DisplayField'
import Form, { FormRequiredText } from '../../../atoms/form/Form'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import Banner from '../../../atoms/banner/Banner'
import Toggle from '../../../atoms/toggle/Toggle'
import ConfirmModal from '../../../organisms/confirm-modal/ConfirmModal'
import { formatPersonName } from '../../../../lib/formatters'

interface Props extends RouteChildrenProps {
  participant?: API.ParticipantResponse
  event: API.EventResponse
}
interface Option {
  value: number
  label: string
}

const createOptions = (num: number): Option[] => {
  const options: Option[] = []
  for (let i = 1; i <= num; i++) {
    options.push({ value: i, label: i.toString() })
  }
  return options
}

const teamOptions = createOptions(100)

function ParticipantForm({ participant, event, match }: Props) {
  const { eventId } = useParams<{ eventId: string }>()

  const [modalOpen, setModalOpen] = useState(false)
  const [redirect, setRedirect] = useState(`/events/${eventId}/view`)

  const [
    { formData: {id, email, firstName, lastName, team, isCeo}, inProgress, fieldErrors, error, completed },
    { setField, setFieldSimple, setDropdownField, handleSubmit }] = useForm(participant || {
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    team: 1,
    isCeo: false
  })

  const callback = useCallback((transferCeo?: boolean) => {
    return () => {
      const payload = {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        team,
        isCeo
      }
      if (!!participant) return API.updateParticipant(eventId, participant.id, payload, transferCeo)
      else return API.addParticipant(eventId, payload, transferCeo)
    }
  }, [eventId, participant, email, firstName, lastName, team, isCeo])

  const validate = useCallback(() => {
    const errors = []
    if (!email) {
      errors.push({ field: 'email', message: 'Enter an email' })
    }
    if (!firstName) {
      errors.push({ field: 'firstName', message: 'Enter a first name' })
    }
    if (!lastName) {
      errors.push({ field: 'lastName', message: 'Enter a last name' })
    }
    return errors
  }, [email, firstName, lastName])

  const existingCeo = useMemo(() => {
    return event.participants.find(p => p.isCeo && p.team === team)
  }, [event.participants, team])

  const onSubmit = useCallback(() => {
    if (existingCeo && isCeo && existingCeo.id !== participant?.id) {
      return setModalOpen(true)
    }
    handleSubmit(callback(), validate)()
  }, [callback, existingCeo, handleSubmit, isCeo, validate, participant])

  const onSubmitPrev = useCallback(() => {
    if (!participant) return
    onSubmit()
    const currentIndex = event.participants.findIndex((p: API.ParticipantResponse) => p.id === participant.id)
    let nextIndex = currentIndex - 1
    if (nextIndex < 0) {
      nextIndex = event.participantsCount - 1
    }
    setRedirect(generatePath(match!.path, {
      eventId,
      participantId: event.participants[nextIndex].id
    }))
  }, [onSubmit, event, eventId, match, participant])

  const onSubmitNext = useCallback(() => {
    if (!participant) return
    onSubmit()
    const currentIndex = event.participants.findIndex((p: API.ParticipantResponse) => p.id === participant.id)
    let nextIndex = currentIndex + 1
    if (nextIndex >= event.participantsCount) {
      nextIndex = 0
    }
    setRedirect(generatePath(match!.path, {
      eventId,
      participantId: event.participants[nextIndex].id
    }))
  }, [onSubmit, event, eventId, match, participant])

  return (
    <VerticalGroup spread full>
      {existingCeo && modalOpen && <ConfirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSubmit(callback(true), validate)}
        confirmInProgress={inProgress}
        title='A CEO for this team already exists'
        description={`If you proceed this will transfer the CEO status from ${formatPersonName(existingCeo!)} to ${formatPersonName({ firstName, lastName })}, do you want to continue?`}
        confirmTitle='Continue'
        error={error && error.message}
        warning
      />}
      <VerticalGroup wide>
        <Banner type='error' active={!!error} message={error?.message} />
        {!!completed && !error && <Redirect to={redirect} />}
        <Form noValidate id='participant-add-form'>
          <FormRequiredText />
          <Input autoFocus data-test='email' type='email' label='Email *' value={email} onChange={setField('email')} error={fieldErrors.email} />
          <Input data-test='firstName' type='text' label='First Name *' value={firstName} onChange={setField('firstName')} error={fieldErrors.firstName} />
          <Input data-test='lastName' type='text' label='Last Name *' value={lastName} onChange={setField('lastName')} error={fieldErrors.lastName} />
          <Dropdown
            label='Team #'
            selectProps={{
              options: teamOptions,
              defaultValue: teamOptions.find(o => o.value === team),
              onChange: setDropdownField('team')
            }}
          />
          <Toggle
            label="CEO"
            helpTitle='What is a CEO?'
            helpDescription='As CEO, there will be certain decisions which can only be entered into your simulation interface'
            checked={isCeo}
            onUpdate={setFieldSimple('isCeo')}
            block
          />
          {!!participant && <DisplayField label='Participant ID' value={id} />}
        </Form>
      </VerticalGroup>
      <InlineGroup block spread>
        <Link data-test='cancel' to={participant ? `/events/${eventId}/participants/${participant.id}/view` : `/events/${eventId}/view`}>
          <Button light wide>Cancel</Button>
        </Link>
        <InlineGroup spread spaceLeft={4}>
          {!!participant && <Button
            data-test='save-previous'
            form='participant-add-form'
            wide
            disabled={!event.participantsCount}
            inProgress={inProgress}
            onClick={onSubmitPrev}
          >{'Save & Previous'}
          </Button>}
          {!!participant && <Button
            data-test='save-next'
            form='participant-add-form'
            wide
            disabled={!event.participantsCount}
            inProgress={inProgress}
            onClick={onSubmitNext}
          >{'Save & Next'}
          </Button>}
          <Button
            data-test='save'
            form='participant-add-form'
            wide
            inProgress={inProgress}
            onClick={onSubmit}
          >Save
          </Button>
        </InlineGroup>
      </InlineGroup>
    </VerticalGroup>
  )
}

export default ParticipantForm
